import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import api, { setupInterceptors } from "../API/axiosInstance";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const role = Array.isArray(user?.role) ? user.role[0] : user?.role || null;
  const isAuthenticated = Boolean(user);

  const clearAuth = useCallback(() => {
    setUser(null);
    localStorage.clear();
    sessionStorage.clear();
  }, []);

  const handleUnauthorized = useCallback(async () => {
    clearAuth();
  }, [clearAuth]);

  const restoreSession = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/user/me", { withCredentials: true });
      setUser(res.data);
    } catch {
      clearAuth();
    } finally {
      setLoading(false);
    }
  }, [clearAuth]);

  const login = useCallback(async (credentials) => {
    await api.post("/user/signin", credentials, { withCredentials: true });
    const res = await api.get("/user/me", { withCredentials: true });
    setUser(res.data);
    return res.data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.get("/user/logout", {
        withCredentials: true,
        _skipUnauthorizedLogout: true,
      });
    } catch {
      // Always clear client auth state, even if logout API fails.
    } finally {
      clearAuth();
    }
  }, [clearAuth]);

  useEffect(() => setupInterceptors(handleUnauthorized), [handleUnauthorized]);

  useEffect(() => {
    if (window.location.pathname === "/login") {
      setLoading(false);
      return;
    }
    restoreSession();
  }, [restoreSession]);

  const value = useMemo(
    () => ({
      user,
      role,
      isAuthenticated,
      loading,
      login,
      logout,
      restoreSession,
    }),
    [user, role, isAuthenticated, loading, login, logout, restoreSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};
